import { useEffect, useRef, useCallback } from 'react';

export default function useInfiniteScroll(callback, hasMore) {
  const callbackRef = useRef(callback);
  const observerRef = useRef(null);
  const nodeRef = useRef(null);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const attach = useCallback(
    (node) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      if (!node || !hasMore) return;

      observerRef.current = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            callbackRef.current();
          }
        },
        { rootMargin: '200px 0px', threshold: 0 },
      );

      observerRef.current.observe(node);
    },
    [hasMore],
  );

  // Which `attach` the live observer was built from. The ref callback runs at
  // commit and the effect right after it, so without this both fire on mount and
  // the first IntersectionObserver is constructed only to be thrown away.
  const attachedRef = useRef(null);

  const ref = useCallback(
    (node) => {
      nodeRef.current = node;
      attachedRef.current = attach;
      attach(node);
    },
    [attach],
  );

  useEffect(() => {
    if (attachedRef.current === attach) return;
    attachedRef.current = attach;
    attach(nodeRef.current);
  }, [attach]);

  useEffect(
    () => () => {
      if (observerRef.current) observerRef.current.disconnect();
    },
    [],
  );

  return ref;
}
